import React, { useCallback, useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { BasicDatePicker } from "../datepicker/datepicker";
import SearchChipExpand from "./search-chip-expand";
import { IconReset, IconSearch, IconTune } from "@mzc-cloudplex/icons";
import { Button, IconButton, TextField, Tooltip, Grid, InputAdornment, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import SearchPopover from "./search-popover";
import TagsInput from "@features/tags-input/tags-input";
import { ChipUsersInput, ChipUsersInputV2 } from "@features/chip-popper-components/chip-users-input";
import {
    getSearchParamsAsset,
    getSearchParamsAuditLogs,
    getSearchParamsCollection,
    getSearchParamsContentSchema,
    getSearchParamsCustomContent,
    getSearchParamsMetadataExports,
    getSearchParamsRoleManagement,
    getSearchParamsVocabulary,
    keywordParam,
} from "@features/list-search/search-params";
import moment from "moment";
import { SPACE_SCOPE, TABLE_LIST_TYPE } from "@constants";
import SearchAttributionsInput from "@features/attributions-input/search-attributions-input";
import Categories from "@routes/categories";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import { Autocomplete } from "@components_v2/autocomplete";
import compareVersions, { VERSION_CM_11330_USERS_TEAMS_ACCESS_V3 } from "@cores/version";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import menuService from "@services/menuService";
import useSearchFilter from "@hooks/useSearchFilter";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";

const SearchTextField = ({
    type,
    isSearch,
    optionValues,
    fields,
    usePopover,
    placeholder,
    searchQueryParams,
    currentCategoryGroup,
    getCustomMetadataOptions,
    onClickSubmit,
    onClickReset,
}) => {
    const { t } = useTranslation();
    const stage = useSelector(({ stage }) => stage);
    const { parseSearchParamsToSearchValue, parseSearchValueToSearchParams } = useSearchFilter(type);
    const [editValue, setEditValue] = useState({}); //type마다 다른 검색 key를 가지는데 일일히 다 상태로 만들기는 너무 많아서 객체로 관리하고자 생성
    const [editTempValue, setEditTempValue] = useState({});
    const [open, setOpen] = useState(false);
    const [decodedSearchParams, setDecodedSearchParams] = useState([]); //type마다 다른 검색 key를 가지는데 일일히 다 상태로 만들기는 너무 많아서 객체로 관리하고자 생성
    const { projectId } = menuService.parse(location.pathname);
    const scope =
        location.pathname.indexOf("/admin/spaces") !== -1 && !projectId ? SPACE_SCOPE.ADMIN : SPACE_SCOPE.PROJECT;

    const ref = useRef();
    const paramRef = useRef(null);

    const onHandleClickTune = () => {
        setOpen(true);
    };

    const onHandleSearchPopperClose = () => {
        setOpen(false);
    };

    const onHandleSubmit = (values) => {
        const searchFields = getSearchFields().reduce((acc, cur) => {
            if (cur.attributions) {
                return [...acc, ...cur.attributions];
            } else return acc;
        }, []);
        const _values = Object.keys(values).reduce((acc, cur) => {
            const type = searchFields.find((item) => item.id === cur)?.type;
            if (type) {
                const _value = parseSearchValueToSearchParams(type, values[cur]);
                return { ...acc, [cur]: _value };
            } else return acc;
        }, {});
        onClickSubmit(_values);
    };

    const onFormikSubmit = (values, actions) => {
        if (onClickSubmit) {
            onHandleSubmit(values);
        }
        actions.setSubmitting(false);
    };

    const getValueByParam = useCallback(
        (values, param, isChip = false, handleChange, handleSubmit) => {
            const { id, label, value, options, type, multiple, placeholder, isCustomField } = param;
            if (!type) return;

            if (isCustomField)
                return (
                    <MetadataFieldEditMode
                        id={id}
                        type={type}
                        value={values[id]}
                        options={options?.map((option) => ({
                            ...option,
                            selected: values[id]?.includes(option.value),
                        }))}
                        onChangeField={(id, value) => {
                            handleChangeValue(id, value);
                        }}
                    />
                );

            if (type === "text")
                return (
                    <TextField
                        sx={{ width: "100%" }}
                        value={values[id]}
                        placeholder={"이름 입력"}
                        onChange={(e) => handleChangeValue(id, e.target.value)}
                    ></TextField>
                );

            if (type === "users")
                return (
                    <>
                        {compareVersions(stage.version, VERSION_CM_11330_USERS_TEAMS_ACCESS_V3) >= 0 ? (
                            <ChipUsersInputV2
                                user={{ id: values[id], name: values[`${id}Name`] }}
                                placeholder={placeholder}
                                onDeleteUser={() => {
                                    handleChangeValue(`${id}Name`, null);
                                    handleChangeValue(id, null);
                                }}
                                onAddUser={(user) => {
                                    handleChangeValue(`${id}Name`, user.name);
                                    handleChangeValue(id, user.id);
                                }}
                                isEdit={false}
                                scope={scope}
                            />
                        ) : (
                            <ChipUsersInput
                                user={{ id: values[id], name: values[`${id}Name`] }}
                                placeholder={placeholder}
                                onDeleteUser={() => {
                                    handleChangeValue(`${id}Name`, null);
                                    handleChangeValue(id, null);
                                }}
                                onAddUser={(user) => {
                                    handleChangeValue(`${id}Name`, user.name);
                                    handleChangeValue(id, user.id);
                                }}
                                isEdit={false}
                            />
                        )}
                    </>
                );
            if (type === "date")
                return (
                    <Grid container flexDirection={"row"} gap={1.5}>
                        <BasicDatePicker
                            width={194}
                            label={t(`common::label::Start`, `Start`)}
                            value={values[`${id}StartAt`] && moment(values[`${id}StartAt`]).format("YYYY-MM-DD")}
                            onChangeValue={(value) => {
                                handleChangeValue(`${id}StartAt`, moment(value).startOf("day").toISOString());
                            }}
                        ></BasicDatePicker>
                        <BasicDatePicker
                            width={194}
                            label={t(`common::label::End`, `End`)}
                            value={values[`${id}EndAt`] && moment(values[`${id}EndAt`]).format("YYYY-MM-DD")}
                            onChangeValue={(value) => {
                                handleChangeValue(`${id}EndAt`, moment(value).endOf("day").toISOString());
                            }}
                        ></BasicDatePicker>
                    </Grid>
                );
            if (type === "dateRange")
                return (
                    <Grid container flexDirection={"row"} gap={1.5}>
                        <BasicDatePicker
                            width={194}
                            label={t(`common::label::Start`, `Start`)}
                            value={value?.start && moment(value?.start)?.format("YYYY-MM-DD")}
                            maxDate={value?.end && moment(value?.end)?.format("YYYY-MM-DD")}
                            onChangeValue={(v) => {
                                if (v?.format("YYYY-MM-DD")) {
                                    const _value = {
                                        ...value,
                                        start: v.startOf("day").toISOString(),
                                    };
                                    handleChangeValue(id, _value);
                                } else {
                                    const _value = {
                                        ...value,
                                        start: null,
                                    };
                                    handleChangeValue(id, _value);
                                }
                            }}
                        ></BasicDatePicker>
                        <BasicDatePicker
                            width={194}
                            label={t(`common::label::End`, `End`)}
                            value={value?.end && moment(value?.end)?.format("YYYY-MM-DD")}
                            minDate={value?.start && moment(value?.start)?.format("YYYY-MM-DD")}
                            onChangeValue={(v) => {
                                if (v?.format("YYYY-MM-DD")) {
                                    const _value = {
                                        ...value,
                                        end: v.endOf("day").toISOString(),
                                    };
                                    handleChangeValue(id, _value);
                                } else {
                                    const _value = {
                                        ...value,
                                        end: null,
                                    };
                                    handleChangeValue(id, _value);
                                }
                            }}
                        ></BasicDatePicker>
                    </Grid>
                );
            if (type === "tags")
                return (
                    <TagsInput
                        tags={values[id]?.map((v) => ({ value: v }))}
                        onDeleteTag={(index) => {
                            handleChangeValue(
                                id,
                                values[id]?.filter((t, idx) => idx !== index).map(({ value }) => value),
                            );
                        }}
                        onAddTag={(tag) => {
                            handleChangeValue(id, values[id] ? values[id].concat(tag?.value) : [tag?.value]);
                        }}
                        isEdit={false}
                    />
                );
            if (type === "attributions") {
                return (
                    <SearchAttributionsInput
                        defaultValues={values[id]}
                        onChangeAttributions={(attributions, validationErrors) => {
                            if (!Object.keys(validationErrors)?.length)
                                handleChangeValue(
                                    "attributions",
                                    attributions.map(({ key, value }) => ({ key, value })),
                                );
                        }}
                    />
                );
            }
            if (type === "categories") {
                return (
                    <Categories
                        tooltipDisabled={false}
                        categoryGroups={false}
                        selectedCategoryGroup={{
                            id: currentCategoryGroup?.id,
                            label: currentCategoryGroup?.name,
                        }} //schema에서 제공받아야 함
                        placeholder={placeholder}
                        type={value?.categoryType}
                        selectedValues={values["categories"]?.map((c) => {
                            return {
                                label: c.name,
                                name: c.name,
                                value: c.id,
                                id: c.id,
                            };
                        })}
                        onChange={(options) => {
                            handleChangeValue(
                                "categories",
                                options.map((option) => ({ id: option.id, name: option.name })),
                            );
                        }}
                    />
                );
            }
            if (type === "select") {
                return (
                    <Autocomplete
                        id={id}
                        multiple={multiple}
                        disableClearable={multiple}
                        disableCloseOnSelect={multiple}
                        value={t(`common::label::${value}`, value)}
                        options={optionValues[id]?.filter(({ value }) => value) ?? []}
                        placeholder={placeholder}
                        onClickOption={(id, value) => handleChangeValue(id, value)}
                    />
                );
            }
        },
        [type, editTempValue],
    );

    const onHandleDeleteChip = (item) => {
        const newParams = { ...searchQueryParams.params };

        if (item.type === "date") {
            delete newParams[`${item.id}StartAt`];
            delete newParams[`${item.id}EndAt`];
        } else {
            delete newParams[item.id];
        }

        searchQueryParams.resetSearchQueryParams({
            action: "push",
            params: newParams,
        });
    };

    const handleChangeValue = (key, value) => {
        paramRef.current.setFieldValue(key, value);
    };

    // chip의 disabled 여부 판단
    const handleChipIsChanged = useCallback(
        (item, values) => {
            if (!item || !searchQueryParams) return false;

            return item.type === "date"
                ? values[`${item.id}StartAt`] === searchQueryParams.params[`${item.id}StartAt`] &&
                      values[`${item.id}EndAt`] === searchQueryParams.params[`${item.id}EndAt`]
                : values[item.id] === searchQueryParams.params[item.id];
        },
        [searchQueryParams?.params],
    );

    const getSearchFields = useCallback(
        (options) => {
            switch (type) {
                case TABLE_LIST_TYPE.CUSTOM_CONTENT:
                    return getSearchParamsCustomContent(fields, getCustomMetadataOptions);
                case TABLE_LIST_TYPE.ASSET:
                    return getSearchParamsAsset();
                case TABLE_LIST_TYPE.COLLECTION:
                    return getSearchParamsCollection();
                case TABLE_LIST_TYPE.VOCABULARY:
                    return getSearchParamsVocabulary();
                case TABLE_LIST_TYPE.METADATA_EXPORTS:
                    return getSearchParamsMetadataExports();
                case TABLE_LIST_TYPE.AUDIT_LOGS:
                    return getSearchParamsAuditLogs(options?.domain, fields);
                case TABLE_LIST_TYPE.ROLE_MANAGEMENT:
                    return getSearchParamsRoleManagement();
                case TABLE_LIST_TYPE.CONTENT_SCHEMA:
                    return getSearchParamsContentSchema();
                default:
                    return [];
            }
        },
        [fields, getCustomMetadataOptions],
    );

    const onHandleClickResetPopover = () => {
        handleChangeValue("name", "");
        searchQueryParams.resetSearchQueryParams({ action: "replace" });
    };

    useEffect(() => {
        if (isSearch) {
            const decodedParams = searchQueryParams.params;
            setEditTempValue({ ...decodedParams });
            setEditValue({ ...decodedParams });
            const paramsForChips = parseSearchParamsToSearchValue(decodedParams);
            setDecodedSearchParams(paramsForChips);
        }
    }, [searchQueryParams.params]);

    return (
        <>
            <Formik
                innerRef={paramRef}
                initialValues={searchQueryParams.params}
                onSubmit={(values, actions) => {
                    const newValues = { ...values, isQuickSearch: false, offset: 0 };
                    onFormikSubmit(newValues, actions);
                }}
                enableReinitialize={true}
            >
                {({ values, handleSubmit, handleChange }) => {
                    const key = keywordParam[type] ?? "name";
                    return (
                        <Grid container flexDirection={"column"}>
                            <>
                                <Grid item sx={{ maxWidth: "756px" }}>
                                    <TextField
                                        className={"area-search"}
                                        ref={ref}
                                        id="search"
                                        type="search"
                                        fullWidth
                                        value={values[key] ?? ""}
                                        variant="filled"
                                        placeholder={placeholder}
                                        onChange={(e) => {
                                            handleChangeValue(key, e.target.value);
                                        }}
                                        onKeyUp={(e) => {
                                            if (e.code === "Enter") {
                                                handleSubmit();
                                            }
                                        }}
                                        sx={{
                                            [theme.breakpoints.down("xl")]: {
                                                width: `100%`,
                                            },
                                            ".MuiFilledInput-root": {
                                                backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconSearch size={16} htmlColor={theme.palette.secondary[800]} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position={"end"}>
                                                    {usePopover && (
                                                        <Tooltip
                                                            title={t(`common::label::search options`, `search options`)}
                                                        >
                                                            <IconButton
                                                                onClick={onHandleClickTune}
                                                                size={"medium"}
                                                                circled
                                                            >
                                                                <IconTune
                                                                    size={20}
                                                                    htmlColor={theme.palette.secondary[800]}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </>
                            {isSearch && (
                                <Grid item mt={2}>
                                    {/* 미검색 결과 */}
                                    <Grid container alignItems={"center"}>
                                        {!decodedSearchParams.length ? (
                                            <Typography
                                                component={"span"}
                                                variant={"body1_400"}
                                                color={theme.palette.greySecondary.main}
                                            >
                                                {t(
                                                    `common::msg::You can see all your current assets. You can find the asset you are looking for using the search option.`,
                                                    `You can see all your current assets. You can find the asset you are looking for using the search option.`,
                                                )}
                                            </Typography>
                                        ) : (
                                            <Grid container flexDirection={"row"} gap={"10px"}>
                                                {decodedSearchParams.map((item, index) => {
                                                    return (
                                                        <SearchChipExpand
                                                            key={`${item.id}-${item.value}`}
                                                            item={item}
                                                            isDisabled={handleChipIsChanged(item, values)}
                                                            onDelete={() => {
                                                                onHandleDeleteChip(item);
                                                            }}
                                                            onCancel={() => {
                                                                setEditTempValue(editValue);
                                                            }}
                                                            onSave={() => {
                                                                handleSubmit();
                                                            }}
                                                            component={getValueByParam(
                                                                values,
                                                                item,
                                                                true,
                                                                handleChange,
                                                                handleSubmit,
                                                            )}
                                                        />
                                                    );
                                                })}
                                                <TooltipButton
                                                    circled
                                                    disabled={!decodedSearchParams.length}
                                                    title={t("common::label::Clear", "Clear")}
                                                    buttonIcon={<IconReset size={16} />}
                                                    color={"greySecondary"}
                                                    onClickButton={onClickReset}
                                                ></TooltipButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    );
                }}
            </Formik>
            {usePopover && (
                <SearchPopover
                    type={type}
                    open={open}
                    scope={scope}
                    optionValues={optionValues}
                    getSearchFields={getSearchFields}
                    currentCategoryGroup={currentCategoryGroup}
                    anchorEl={ref.current}
                    onClickClose={onHandleSearchPopperClose}
                    isSearch={isSearch}
                    searchQueryParams={searchQueryParams}
                    onClickSubmit={onHandleSubmit}
                    onClickReset={onHandleClickResetPopover}
                    decodedSearchParams={decodedSearchParams}
                />
            )}
        </>
    );
};

export default SearchTextField;
